import React from 'react';
import { graphql } from 'gatsby';
import classNames from 'classnames';

import Main from '../components/Product/Main';
import SEO from '../components/SEO';
import RichData from '../components/RichData';

import renderComponent from '../utils/renderComponent';
import { getMetaImageFromShopifyMedia } from '../utils';
import Recommendations from '../components/Recommendations';

const ns = `page-product`;

const Product = ({
	location,
	data: {
		shopifyProduct: product,
		contentfulGlobalSettings,
		contentfulProduct,
	},
}) => {
	const components = contentfulProduct?.pageComponents;
	const shouldRenderDefaultRecommendations =
		!!contentfulGlobalSettings?.recommendedItemsEnabledProduct &&
		!components?.find((component) => {
			return (
				component?.typename === `ContentfulPageComponentRecommendedProducts`
			);
		});

	const { title, handle, description, media } = product;

	const metaImage = getMetaImageFromShopifyMedia(media);

	const rootClassnames = classNames({
		[`${ns}`]: true,
		[`${ns}-${handle}`]: true,
	});

	//  ------
	//  Render
	//  ------
	return (
		<div className={rootClassnames}>
			<SEO
				title={title}
				image={metaImage?.image?.src}
				description={description}
			/>
			<RichData product={product} />
			<Main
				location={location}
				product={product}
				globalSettings={contentfulGlobalSettings}
			/>

			{shouldRenderDefaultRecommendations && (
				<Recommendations
					productIds={product?.shopifyId}
					variantIds={product?.variants?.map((v) => {
						return v.shopifyId;
					})}
					title={`You May Also Like`}
				/>
			)}

			{components?.length > 0 &&
				components.map((component) => {
					return renderComponent(component, product);
				})}
		</div>
	);
};

export default Product;

export const query = graphql`
	query ($storefrontId: String!, $codedId: String!) {
		shopifyProduct(storefrontId: { eq: $storefrontId }) {
			shopifyId
			storefrontId
			title
			handle
			description
			descriptionHtml
			tags
			productType
			featuredImage {
				src
			}
			metafields {
				key
				value
				namespace
			}
			priceRange: priceRangeV2 {
				maxVariantPrice {
					amount
					currencyCode
				}
				minVariantPrice {
					amount
					currencyCode
				}
			}
			media {
				mediaContentType
				id
				... on ShopifyMediaImage {
					image {
						altText
						src
						gatsbyImageData(width: 900, height: 1128)
					}
				}
				... on ShopifyVideo {
					id
					alt
					sources {
						mimeType
						url
					}
					preview {
						image {
							altText
							gatsbyImageData(width: 900, height: 1128)
						}
					}
				}
				... on ShopifyExternalVideo {
					id
					alt
					embeddedUrl
					preview {
						image {
							altText
							gatsbyImageData(width: 900, height: 1128)
						}
					}
				}
			}
			variants {
				shopifyId
				availableForSale
				storefrontId
				title
				price
				compareAtPrice
				sku
				inventoryQuantity
				position
				selectedOptions {
					name
					value
				}
			}
			options {
				name
				values
				id: shopifyId
			}
		}
		contentfulGlobalSettings {
			productBenefits {
				name
				description {
					raw
				}
				icon {
					gatsbyImageData(width: 50, height: 50)
					title
				}
			}
			recommendedItemsEnabledProduct
			rechargeSeeDetailsUrl
		}
		contentfulProduct(storefrontId: { eq: $codedId }) {
			pageComponents {
				... on ContentfulPageComponentHero {
					typename: __typename
					title {
						raw
					}
					description {
						raw
					}
					contentAlignment
					textAlignment
					textColor
					links {
						text
						style
						link
						withArrow
					}
					backgroundImage {
						...BackgroundImageFragment
					}
				}
				... on ContentfulPageComponentProductDetails {
					typename: __typename
					column1Title {
						raw
					}
					column1ArticleTitle
					column1ArticleText {
						raw
					}
					column2Title {
						raw
					}
					column2ArticleTitle
					column2ArticleText {
						raw
					}
					column3Title {
						raw
					}
					column3Accordion {
						label
						labelIcon
						text {
							raw
						}
						image {
							gatsbyImageData(layout: FULL_WIDTH)
						}
					}
				}
				... on ContentfulPageComponent5050 {
					typename: __typename
					eyebrow
					title {
						raw
					}
					description {
						raw
					}
					quote {
						raw
					}
					quoteAuthor
					links {
						text
						style
						link
						withArrow
					}
					image {
						gatsbyImageData(layout: FULL_WIDTH)
					}
					textAlignment
					textColor
					height
					layout
					imagePadding
					backgroundColor
					backgroundColorHex {
						value
						name
					}
				}
				... on ContentfulPageComponentIconGrid {
					typename: __typename
					title {
						raw
					}
					description {
						raw
					}
					icons {
						title {
							raw
						}
						image {
							gatsbyImageData(layout: CONSTRAINED)
						}
						imageSize
						description {
							raw
						}
					}
					textColor
					mobileLayout
					backgroundColor
					backgroundColorHex {
						value
						name
					}
				}
				... on ContentfulPageComponentTickerTape {
					typename: __typename
					marqueeItems
					textColor
					backgroundColor
					direction
					iconImage {
						url
						title
					}
				}
				... on ContentfulPageComponentFeaturedGrid {
					typename: __typename
					eyebrow
					title {
						raw
					}
					description {
						raw
					}
					items {
						title {
							raw
						}
						description {
							raw
						}
						textAlignment
						textColor
						links {
							text
							style
							link
							withArrow
						}
						backgroundImage {
							gatsbyImageData(layout: FULL_WIDTH)
						}
					}
					textAlignment
					textColor
					layout
					backgroundColor
					backgroundColorHex {
						value
						name
					}
				}
				... on ContentfulPageComponentProductSlider {
					typename: __typename
					eyebrow
					title {
						raw
					}
					description {
						raw
					}
					coverTitle
					coverDescription
					coverImage {
						gatsbyImageData(width: 450, height: 564)
					}
					productReferences
					collectionReference
				}
				... on ContentfulPageComponentProductGrid {
					typename: __typename
					eyebrow
					title {
						raw
					}
					description {
						raw
					}
					productReferences
					collectionReference
					promoCards {
						...PromoCardFragment
					}
				}
				... on ContentfulPageComponentBannerShort {
					typename: __typename
					title {
						raw
					}
					description {
						raw
					}
					contentAlignment
					textAlignment
					textColor
					quote {
						raw
					}
					quoteAuthor
					links {
						text
						style
						link
						withArrow
					}
					backgroundImage {
						...BackgroundImageFragment
					}
					backgroundColor
					backgroundColorHex {
						value
						name
					}
					useBackgroundImageHeight
				}
				... on ContentfulPageComponentSplitColumns {
					typename: __typename
					eyebrow
					title {
						raw
					}
					description {
						raw
					}
					columns {
						title {
							raw
						}
						description {
							raw
						}
						textAlignment
						textColor
						links {
							text
							style
							link
							withArrow
						}
						backgroundImage {
							gatsbyImageData(layout: FULL_WIDTH)
						}
					}
					textAlignment
					textColor
					backgroundColor
					backgroundColorHex {
						value
						name
					}
				}
				... on ContentfulPageComponentVideo {
					typename: __typename
					videoUrl
					videoUrlMobile
					video {
						url
					}
					videoMobile {
						url
					}
					videoTitle: title {
						raw
					}
					videoDescription: description {
						raw
					}
					previewImage {
						gatsbyImageData(layout: FULL_WIDTH)
					}
					previewImageMobile {
						gatsbyImageData(layout: FULL_WIDTH)
					}
				}
				... on ContentfulPageComponentImageCarousel {
					typename: __typename
					aspectRatio
					carouselTitle
					ctaLink
					ctaLabel
					images {
						altText
						link
						image {
							gatsbyImageData(layout: FULL_WIDTH)
						}
					}
				}
				... on ContentfulPageComponentGifModule {
					typename: __typename
					id
					gifModuleTitle: title
					gifModuleSubtitle: subtitle
					tiles {
						id
						title
						subtitle
						altText
						link
						media {
							title
							filename
							url
							description
							gatsbyImageData(layout: FULL_WIDTH)
						}
					}
				}
				... on ContentfulPageComponentAccordion {
					typename: __typename
					title {
						raw
					}
					items {
						labelIcon
						label
						text {
							raw
						}
						image {
							gatsbyImageData(layout: FULL_WIDTH)
						}
					}
				}
				... on ContentfulPageComponentProductReviews {
					typename: __typename
				}
				... on ContentfulPageComponentRecommendedProducts {
					typename: __typename
				}
				... on ContentfulPageComponentCompareProducts {
					typename: __typename
					title {
						raw
					}
					description {
						raw
					}
					products
					collection
				}
				... on Node {
					... on ContentfulPageComponentFireworkFeed {
						typename: __typename
						html {
							raw
						}
						scriptSourceUrl
					}
				}
				... on Node {
					... on ContentfulPageComponentFourSixtyInstagram {
						typename: __typename
						scriptSourceUrl
						dataFeedId
						dataTheme
						dataConnectorFilter
						dataCellSize
						styleTag {
							raw
						}
					}
				}
				... on Node {
					... on ContentfulPageComponentMarkdown {
						typename: __typename
						title {
							raw
						}
						description {
							raw
						}
						markdown {
							raw
						}
					}
				}
			}
		}
	}
`;
